<template>
    <div class="container-fluid main-color">
        <div class="row">
            <div class="col-12">
                <div class="page-title-box">
                    <h4 class="page-title">Edit Assessment Module</h4>
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb m-0">
                            <li class="breadcrumb-item">
                                <router-link :to="{ name: 'after-month-trainer-question-assessment' }">
                                    Self-Assessment Modules
                                </router-link>
                            </li>
                            <li class="breadcrumb-item active" aria-current="page">Edit Module</li>
                        </ol>
                    </nav>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-lg-12">
                <place-holder v-if="loading || initialLoading"></place-holder>
                <div class="card">
                    <div v-if="!loading && !initialLoading" class="card-body">
                        <form class="parsley-examples" @submit.prevent="updateAssessmentModule">
                            <!-- Module Name -->
                            <div class="row mb-3">
                                <div class="col-md-3">
                                    <label class="form-label fw-semibold">Module <span
                                            class="text-danger">*</span></label>
                                </div>
                                <div class="col-md-6">
                                    <v-select v-model="module.name" label="name" :options="moduleList"
                                        :clearable="false" :disabled="true"
                                        :class="{ 'p-invalid': v$.module.name.$error, }"></v-select>
                                    <v-errors
                                        :vuelidateErrors="{ errors: v$.module.name.$errors, value: 'Module name', }"></v-errors>
                                </div>
                            </div>

                            <!-- Questions Section Header -->
                            <div class="row mb-2">
                                <div class="col-md-12">
                                    <div class="pb-2">
                                        <h5 class="mb-0 text-primary">Assessment Questions</h5>
                                    </div>
                                </div>
                            </div>

                            <!-- Questions List -->
                            <div v-for="(question, index) in module.questions" :key="question.id || `new-${index}`"
                                class="question-item mb-3">
                                <!-- Question Text -->
                                <div class="row mb-2 align-items-start">
                                    <div class="col-md-3">
                                        <label class="form-label fw-semibold">
                                            {{ index + 1 }}. Question Name <span class="text-danger">*</span>
                                        </label>
                                    </div>

                                    <div class="col-md-6">
                                        <textarea class="form-control" v-model="question.question"
                                            placeholder="Enter the question" rows="2"
                                            :class="{ 'is-invalid': hasQuestionError(index, 'question') }"></textarea>
                                        <v-errors v-if="hasQuestionError(index, 'question')"
                                            :vuelidateErrors="{ errors: getQuestionErrors(index, 'question'), value: 'Question text' }"></v-errors>
                                    </div>

                                    <!-- Active Button at the end -->
                                    <div class="col-md-3 d-flex justify-content-end">
                                        <button v-if="question.id" type="button" @click="toggleQuestionStatus(question)"
                                            :class="['btn btn-sm', question.status === 1 ? 'btn-success' : 'btn-secondary']"
                                            :title="question.status === 1 ? 'Click to deactivate' : 'Click to activate'"
                                            :disabled="statusLoading[question.id]">
                                            <i v-if="statusLoading[question.id]" class="fas fa-spinner fa-spin"></i>
                                            <template v-else>
                                                <i v-if="question.status === 1" class="fas fa-toggle-on me-1"></i>
                                                <i v-else class="fas fa-toggle-off me-1"></i>
                                                {{ question.status === 1 ? 'Active' : 'Inactive' }}
                                            </template>
                                        </button>
                                    </div>
                                </div>


                                <div class="row mb-2 align-items-center">
                                    <div class="col-md-3">
                                        <label class="form-label fw-semibold ms-2">Answer Type <span
                                                class="text-danger">*</span></label>
                                    </div>

                                    <div class="col-md-6">
                                        <select v-if="question.id" v-model="question.type" class="form-select" disabled>
                                            <option value="typing">Answer the question</option>
                                            <option value="radio">Multiple Choice</option>
                                            <option value="true_false">True / False</option>
                                            <option value="fill_in_blank">Fill in the Blank</option>
                                        </select>

                                        <select v-else v-model="question.type" class="form-select"
                                            @change="onQuestionTypeChange(question)">
                                            <option value="typing">Answer the question</option>
                                            <option value="radio">Multiple Choice</option>
                                            <option value="true_false">True / False</option>
                                            <option value="fill_in_blank">Fill in the Blank</option>
                                        </select>
                                    </div>

                                    <div class="col-md-3 text-end">
                                        <div class="d-flex justify-content-end">

                                            <!-- Delete (new questions only) -->
                                            <button v-if="!question.id && module.questions.length > 1" type="button"
                                                @click="removeQuestion(index)" class="btn btn-sm me-1">
                                                <i class="fas fa-trash-alt"></i>
                                            </button>

                                            <!-- Add (only last question) -->
                                            <button v-if="index === module.questions.length - 1" type="button"
                                                @click="addQuestion()" class="btn btn-primary btn-sm">
                                                <i class="mdi mdi-plus"></i> Add
                                            </button>

                                        </div>
                                    </div>
                                </div>


                                <!-- Radio Options Section -->
                                <div v-if="question.type === 'radio' || question.type === 'true_false'">
                                    <div class="row mb-2">
                                        <div class="col-md-3">
                                            <label class="form-label fw-semibold ms-2">Options <span
                                                    class="text-danger">*</span>
                                            </label>
                                        </div>
                                        <div class="col-md-6">
                                            <div v-for="(option, optIndex) in question.options" :key="optIndex"
                                                class="option-item mb-2">
                                                <div class="d-flex align-items-center">
                                                    <div class="form-check me-2">
                                                        <!-- Disable radio button selection for existing questions with answers -->
                                                        <input type="radio" :name="'question_' + index"
                                                            v-model="question.correct_option_index" :value="optIndex"
                                                            class="form-check-input"
                                                            :id="'option_' + index + '_' + optIndex"
                                                            :class="{ 'is-invalid': hasQuestionError(index, 'correct_option_index'), }"
                                                            :disabled="question.id && question.answer && question.answer !== 'null'"
                                                            :title="question.id && question.answer && question.answer !== 'null' ? 'Cannot change answer for existing questions with answers' : ''" />

                                                    </div>
                                                    <div class="flex-grow-1">
                                                        <textarea type="text" class="form-control" v-model="option.name"
                                                            :placeholder="'Enter option ' + (optIndex + 1)"
                                                            :class="{ 'is-invalid': hasOptionError(index, optIndex), }" />
                                                    </div>
                                                    <button v-if="!question.id && question.options.length > 2"
                                                        type="button" @click="removeOption(question, optIndex)"
                                                        class="btn btn-sm ms-2">
                                                        <i class="fas fa-times"></i>
                                                    </button>
                                                </div>
                                                <v-errors v-if="hasOptionError(index, optIndex)"
                                                    :vuelidateErrors="{ errors: getOptionErrors(index, optIndex), value: 'Option text', }"></v-errors>
                                            </div>
                                            <div v-if="!question.id" class="d-flex align-items-center mt-1">
                                                <button type="button" @click="addOption(question)"
                                                    class="btn btn-outline-secondary btn-sm me-2">
                                                    <i class="mdi mdi-plus"></i> Add Option
                                                </button>
                                                <small class="text-muted">Minimum 2 options required</small>
                                            </div>

                                            <div v-if="question.options.length < 2"
                                                class="alert alert-warning py-1 px-2 mt-1">
                                                <small><i class="fas fa-exclamation-triangle me-1"></i>Radio questions
                                                    must have at least 2
                                                    options</small>
                                            </div>

                                            <v-errors v-if="hasQuestionError(index, 'correct_option_index')"
                                                :vuelidateErrors="{ errors: getQuestionErrors(index, 'correct_option_index'), value: 'Correct answer', }"
                                                class="mt-1"></v-errors>
                                        </div>
                                    </div>
                                </div>

                                <!-- Existing Answer Display -->
                                <div v-if="question.id && question.type === 'typing' && question.answer && question.answer !== 'null'"
                                    class="row mb-2">
                                    <div class="col-md-3">
                                        <label class="form-label fw-semibold text-muted">Existing Answers</label>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="alert alert-warning py-1 px-2">
                                            <small><i class="fas fa-exclamation-triangle me-1"></i>This question already
                                                has answers. Editing may affect
                                                existing data.</small>
                                        </div>
                                    </div>
                                </div>
                                <hr>
                            </div>

                            <!-- Action Buttons -->
                            <div class="row mt-3">
                                <div class="col-12">
                                    <div class="text-center">

                                        <!-- Cancel Button -->
                                        <router-link :to="{ name: 'after-month-trainer-question-assessment' }">
                                            <button type="button" class="btn btn-secondary me-3">
                                                Cancel
                                            </button>
                                        </router-link>

                                        <!-- Save Button -->
                                        <button v-if="hasPermissions(['self-assessment-question-edit'])"
                                            :disabled="loading" type="submit" class="btn btn-success px-3">
                                            <span v-if="loading" class="spinner-border spinner-border-sm me-1"
                                                role="status" aria-hidden="true"></span>
                                            {{ loading ? 'Updating...' : 'Save' }}
                                        </button>

                                    </div>
                                </div>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useToast } from "vue-toastification";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import validationErrors from "../../../mixins/validationErrors";
import userHasPermissions from "../../../mixins/userHasPermissions";

export default {
    setup() {
        const toast = useToast();
        return { v$: useVuelidate(), toast };
    },
    components: {
        vSelect,
    },
    mixins: [validationErrors, userHasPermissions],
    data() {
        return {
            selected: {},
            moduleList: [],
            initialLoading: true,
            module: {
                name: "",
                assessment_duration: "30",
                questions: [],
            },
            originalModuleData: null,
            loading: false,
            statusLoading: {}, // Track loading state for individual question status toggles
            baseUrl: process.env.VUE_APP_BASE_URL,
        };
    },
    methods: {
        async getModuleDetails() {
            this.initialLoading = true;
            const moduleId = this.$route.params.module_id;

            try {
                const response = await axios.get(`${this.baseUrl}admin/v3/self-assessment-questions/${this.$route.params.module_id}`);
                const allQuestions = response.data.data || response.data;
                const moduleQuestions = allQuestions.filter(q => q.module_id == moduleId);
                console.log("Fetched module questions:", moduleQuestions);
                if (moduleQuestions.length > 0) {
                    const firstQuestion = moduleQuestions[0];
                    const formattedQuestions = moduleQuestions.map(q => {
                        const question = {
                            id: q.id,
                            question: q.question,
                            type: q.type,
                            answer: q.answer,
                            status: q.status ?? 1,
                            correct_option_index: null,
                            options: []
                        };

                        if (q.type === 'radio' && q.attributes && q.attributes.length > 0) {
                            question.options = q.attributes.map((attr, index) => ({
                                id: attr.id,
                                name: attr.name,
                            }));

                            if (q.answer && q.answer !== 'null') {
                                const correctOptionId = parseInt(q.answer);
                                const correctIndex = question.options.findIndex(opt => opt.id === correctOptionId);
                                if (correctIndex !== -1) {
                                    question.correct_option_index = correctIndex;
                                }
                            }
                        }

                        if (q.type === 'true_false') {
                            question.options = [
                                { id: q.attributes[0]?.id, name: "True" },
                                { id: q.attributes[1]?.id, name: "False" }
                            ];

                            if (q.answer) {
                                const correctIndex = question.options.findIndex(opt => opt.id === parseInt(q.answer));
                                question.correct_option_index = correctIndex !== -1 ? correctIndex : null;
                            } else {
                                question.correct_option_index = null;
                            }
                        }

                        return question;
                    });

                    await this.getModuleList();
                    const moduleInfo = this.moduleList.find(m => m.id == moduleId);

                    this.module = {
                        name: moduleInfo || { id: moduleId, name: `Module ${moduleId}` },
                        assessment_duration: firstQuestion.assessment_duration || "",
                        questions: formattedQuestions
                    };

                    this.originalModuleData = JSON.parse(JSON.stringify(this.module));
                } else {
                    this.toast.error("No questions found for this module");
                    this.$router.push({ name: 'assessment-module' });
                }
            } catch (error) {
                console.error("Error loading module details:", error);
                this.toast.error("Failed to load module details");
                this.$router.push({ name: 'assessment-module' });
            } finally {
                this.initialLoading = false;
            }
        },

        async getModuleList() {
            try {
                const response = await axios.get(`${this.baseUrl}admin/v1/modules`);
                this.moduleList = response.data.data || response.data;
            } catch (error) {
                console.error("Error loading modules:", error);
                this.toast.error("Failed to load modules list");
            }
        },

        addQuestion() {
            this.module.questions.push({
                question: "",
                type: "typing",
                status: 1, // New questions default to active
                options: [],
                correct_option_index: null,
            });
        },

        removeQuestion(index) {
            if (this.module.questions.length > 1) {
                this.module.questions.splice(index, 1);
                this.toast.info("Question removed");
            } else {
                this.toast.error("At least one question is required");
            }
        },

        onQuestionTypeChange(question) {
            if (question.type === 'typing' || question.type === 'fill_in_blank') {
                question.options = [];
                question.correct_option_index = null;
            } else if (question.type === 'radio' && question.options.length === 0) {
                question.options = [
                    { name: "" },
                    { name: "" }
                ];
                question.correct_option_index = null;
            } else if (question.type === 'true_false') {
                // Pre-fill True/False options
                question.options = [
                    { name: "True" },
                    { name: "False" }
                ];
                question.correct_option_index = null;
            }
        },

        addOption(question) {
            if (question.type === 'radio') {
                question.options.push({ name: "" });
            }
        },

        removeOption(question, optionIndex) {
            if (question.options.length > 2) {
                if (question.correct_option_index === optionIndex) {
                    question.correct_option_index = null;
                } else if (question.correct_option_index > optionIndex) {
                    question.correct_option_index -= 1;
                }
                question.options.splice(optionIndex, 1);
            } else {
                this.toast.error("Radio questions must have at least 2 options");
            }
        },

        async toggleQuestionStatus(question) {
            if (!question.id) return;

            try {
                // Set loading state for this specific question
                this.statusLoading[question.id] = true;

                const newStatus = question.status === 1 ? 0 : 1;

                const response = await axios.patch(
                    `${this.baseUrl}admin/v3/self-assessment-questions/${question.id}/status`,
                    { status: newStatus },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json"
                        }
                    }
                );

                // Update question status
                question.status = newStatus;

                // Show success message
                const statusText = newStatus === 1 ? 'activated' : 'deactivated';
                this.toast.success(`Question ${statusText} successfully!`);

            } catch (error) {
                console.error("Error toggling question status:", error);

                if (error.response && error.response.status === 422) {
                    const errors = error.response.data.errors;
                    let errorMessage = "Validation Error!";

                    if (errors) {
                        Object.keys(errors).forEach(key => {
                            errorMessage += `\n${key}: ${errors[key].join(", ")}`;
                        });
                    }

                    this.toast.error(errorMessage);
                } else if (error.response && error.response.status === 500) {
                    this.toast.error(error.response.data.message || "Server error occurred");
                } else {
                    this.toast.error(error.response?.data?.message || "Failed to update question status");
                }
            } finally {
                // Clear loading state
                this.statusLoading[question.id] = false;
            }
        },

        async updateAssessmentModule() {
            const isFormCorrect = await this.v$.$validate();
            if (!isFormCorrect) {
                this.toast.error("Please fill all required fields correctly!");
                return;
            }

            // Validation for existing questions with answers
            for (const question of this.module.questions) {
                if (!question.question || !question.question.trim()) {
                    this.toast.error("All question fields must be filled");
                    return;
                }

                if (!question.type) {
                    this.toast.error("All questions must have a type");
                    return;
                }

                // Existing questions with answers skip correct_option_index validation for radio or true/false
                if ((question.type === "radio" || question.type === "true_false") && question.id && question.answer && question.answer !== 'null') {
                    const options = question.options || [];
                    if (options.length < 2) {
                        this.toast.error(`Question "${question.question}" must have at least 2 options`);
                        return;
                    }
                    for (const opt of options) {
                        if (!opt.name || !opt.name.trim()) {
                            this.toast.error("All option fields must be filled");
                            return;
                        }
                    }
                    continue;
                }

                // Validation for radio and true/false
                if (question.type === "radio" || question.type === "true_false") {
                    const options = question.options || [];
                    if (options.length < 2) {
                        this.toast.error(`Question "${question.question}" must have at least 2 options`);
                        return;
                    }
                    if (question.correct_option_index === null || question.correct_option_index === undefined) {
                        this.toast.error(`Please select a correct answer for question "${question.question}"`);
                        return;
                    }
                    for (const opt of options) {
                        if (!opt.name || !opt.name.trim()) {
                            this.toast.error("All option fields must be filled");
                            return;
                        }
                    }
                }
            }

            const updateData = {
                module_id: this.module.name.id,
                assessment_duration: this.module.assessment_duration,
                questions: this.module.questions.map(q => {
                    const qData = {
                        question: q.question,
                        type: q.type,
                        options: []
                    };

                    if (q.id) qData.id = q.id;

                    if ((q.type === "radio" || q.type === "true_false") && q.options && q.options.length > 0) {
                        if (q.id && q.answer && q.answer !== 'null') {
                            if (q.type === 'true_false') {
                                qData.options = q.options.map((opt, index) => ({
                                    name: index === 0 ? 'True' : 'False',
                                    is_correct: (q.answer.toLowerCase() === opt.name.toLowerCase())
                                }));
                            } else if (q.type === 'radio') {
                                qData.options = q.options.map((opt, index) => ({
                                    name: opt.name,
                                    is_correct: opt.id ? (parseInt(q.answer) === opt.id) : false,
                                    ...(opt.id ? { id: opt.id } : {})
                                }));
                            }
                        } else {
                            // New questions or no answer yet
                            qData.options = q.options.map((opt, index) => {
                                let isCorrect = index === (q.correct_option_index ?? 0);

                                // For True/False, map index to 'True' / 'False'
                                if (q.type === 'true_false') {
                                    opt.name = index === 0 ? 'True' : 'False';
                                }

                                return {
                                    name: opt.name,
                                    is_correct: isCorrect,
                                    ...(opt.id ? { id: opt.id } : {})
                                };
                            });
                        }
                    }

                    return qData;
                })
            };

            this.loading = true;
            this.$Progress.start();

            try {
                const response = await axios.put(
                    `${this.baseUrl}admin/v3/self-assessment-questions/${this.module.name.id}`,
                    updateData,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json"
                        }
                    }
                );

                this.toast.success("Assessment module updated successfully!");
                this.$router.push({ name: "after-month-trainer-question-assessment" });
            } catch (error) {
                console.error("Update error:", error);
                console.error("Error response:", error.response);

                if (error.response && error.response.status === 422) {
                    const errors = error.response.data.errors;
                    let errorMessage = "Validation Error!";

                    if (errors) {
                        Object.keys(errors).forEach(key => {
                            errorMessage += `\n${key}: ${errors[key].join(", ")}`;
                        });
                    }

                    this.toast.error(errorMessage);
                } else if (error.response && error.response.status === 500) {
                    this.toast.error(error.response.data.message || "Server error occurred");
                } else {
                    this.toast.error(error.response?.data?.message || "Failed to update assessment module");
                }
            } finally {
                this.loading = false;
                this.$Progress.finish();
            }
        },

        hasQuestionError(questionIndex, field) {
            if (!this.v$?.module?.questions?.$each?.$response?.$errors) return false;
            const questionErrors = this.v$.module.questions.$each.$response.$errors[questionIndex];
            return questionErrors && questionErrors[field] && questionErrors[field].length > 0;
        },

        getQuestionErrors(questionIndex, field) {
            if (!this.v$?.module?.questions?.$each?.$response?.$errors) return [];
            const questionErrors = this.v$.module.questions.$each.$response.$errors[questionIndex];
            return questionErrors && questionErrors[field] ? questionErrors[field] : [];
        },

        hasOptionError(questionIndex, optionIndex) {
            if (!this.v$?.module?.questions?.$each?.$response?.$errors) return false;
            const questionErrors = this.v$.module.questions.$each.$response.$errors[questionIndex];
            if (!questionErrors || !questionErrors.options?.$each?.$response?.$errors) return false;
            const optionErrors = questionErrors.options.$each.$response.$errors[optionIndex];
            return optionErrors && optionErrors.name && optionErrors.name.length > 0;
        },

        getOptionErrors(questionIndex, optionIndex) {
            if (!this.v$?.module?.questions?.$each?.$response?.$errors) return [];
            const questionErrors = this.v$.module.questions.$each.$response.$errors[questionIndex];
            if (!questionErrors || !questionErrors.options?.$each?.$response?.$errors) return [];
            const optionErrors = questionErrors.options.$each.$response.$errors[optionIndex];
            return optionErrors && optionErrors.name ? optionErrors.name : [];
        },
    },

    validations() {
        return {
            module: {
                name: { required },
                assessment_duration: { required },
                questions: {
                    $each: {
                        question: { required },
                        type: { required },
                        options: {
                            $each: {
                                name: { required }
                            },
                            $validator: (value, { type }) => {
                                if (type === 'radio') {
                                    return value.length >= 2;
                                }
                                return true;
                            },
                            $message: 'Radio questions must have at least 2 options'
                        },
                        correct_option_index: {
                            $validator: (value, { type, options, answer, id }) => {
                                // Skip validation for existing questions with answers
                                if (id && answer && answer !== 'null') {
                                    return true;
                                }

                                if (type === 'radio') {
                                    return value !== null && value !== undefined && value >= 0 && value < options.length;
                                }
                                return true;
                            },
                            $message: 'Please select a correct answer'
                        }
                    }
                }
            }
        };
    },

    async created() {
        await this.getModuleDetails();
    },
};
</script>

<style scoped>
.question-item:last-child {
    border-bottom: none !important;
}

.option-item:last-child {
    margin-bottom: 0 !important;
}

.v-select.disabled .vs__dropdown-toggle {
    background-color: #e9ecef;
    opacity: 1;
    cursor: not-allowed;
}

.v-select.disabled .vs__clear,
.v-select.disabled .vs__open-indicator {
    display: none;
}

.form-select:disabled {
    background-color: #e9ecef;
    opacity: 1;
    cursor: not-allowed;
}

.form-select:enabled {
    background-color: white;
    cursor: pointer;
}

/* Disabled radio button styling */
input[type="radio"]:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.btn-secondary {
    background-color: #6c757d;
    border-color: #6c757d;
}

/* Reduced spacing */
.row.mb-4 {
    margin-bottom: 1rem !important;
}

.row.mb-3 {
    margin-bottom: 0.75rem !important;
}

.row.mb-2 {
    margin-bottom: 0.5rem !important;
}

.mb-4 {
    margin-bottom: 1rem !important;
}

.mb-3 {
    margin-bottom: 0.75rem !important;
}

.mb-2 {
    margin-bottom: 0.5rem !important;
}

.mt-3 {
    margin-top: 0.75rem !important;
}

.mt-2 {
    margin-top: 0.5rem !important;
}

.mt-1 {
    margin-top: 0.25rem !important;
}

.pb-2 {
    padding-bottom: 0.5rem !important;
}

.py-5 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
}

.py-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
}

.py-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
}

.px-4 {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
}

.px-2 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
}

/* Status toggle button tooltip */
[title] {
    position: relative;
}

[title]:hover::after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    margin-bottom: 5px;
}
</style>